/**
 * Permit Wall arrival orchestration (DISPLAY-only office TV).
 *
 * Watches the top-zone rows across polls and, when a transaction newly
 * changes (its `lastChangedAt` advances) AND its latest update carries an
 * event (موافقة / عدم موافقة / تكليف), it:
 *   • marks that single card as the one flashing - and ONLY that card, so
 *     a fresh arrival implicitly clears any earlier flash;
 *   • fires the matching distinct sound exactly once.
 *
 * Modeled on `useDashboardSounds`: it skips the first poll (loading the
 * page is not an "arrival"), de-duplicates via a seen-map keyed by
 * transaction id → lastChangedAt, and keeps the map current even for
 * non-event changes so they don't re-fire later. When several cards
 * change in one poll, only the newest (max `lastChangedAt`) flashes and
 * sounds, matching the brief's "only the newest arrived card can ever be
 * flashing".
 */

import { useEffect, useRef, useState } from "react";
import type { WallRow } from "@/lib/wall";
import type { WallEvent } from "../../../../packages/keywords/index.js";
import { playKeywordSound, type PlaybackPolicy } from "@/lib/sounds";

/** ~8 s: seven 1.15 s throbs, matching the CSS `wallFlash` iteration count. */
const FLASH_MS = 8050;

/**
 * The wall always wants to be audible - it is an unattended display with
 * no per-keyword UI - so we pass a fixed always-on policy rather than the
 * device sound store. The browser autoplay gate (one tap to unlock) still
 * applies; that is the only thing that can keep it silent.
 */
const WALL_SOUND_POLICY: PlaybackPolicy = {
  masterEnabled: true,
  perKeywordEnabled: { APPROVAL: true, REJECTION: true, ASSIGNMENT: true },
  volume: 85,
};

export interface WallFlash {
  flashId: string | null;
  flashEvent: WallEvent | null;
}

const NO_FLASH: WallFlash = { flashId: null, flashEvent: null };

export function useWallArrivals(rows: WallRow[], ready: boolean): WallFlash {
  const [flash, setFlash] = useState<WallFlash>(NO_FLASH);
  // id -> lastChangedAt seen on the previous poll. `null` until the first
  // poll is processed so we can skip the page-load baseline.
  const seenRef = useRef<Map<string, string | null> | null>(null);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!ready) return;

    const current = new Map(rows.map((r) => [r.id, r.lastChangedAt]));

    if (seenRef.current === null) {
      // First poll after mount: establish the baseline, never flash.
      seenRef.current = current;
      return;
    }

    const seen = seenRef.current;
    // An arrival is any row whose lastChangedAt is new or has advanced.
    const arrivals = rows.filter(
      (r) => r.lastChangedAt && seen.get(r.id) !== r.lastChangedAt,
    );
    // Record every change (event-bearing or not) so a later poll doesn't
    // treat a quiet update as a fresh arrival.
    seenRef.current = current;

    // Only event-bearing arrivals flash/sound; the newest one wins.
    const newest = arrivals
      .filter((r) => r.event !== null)
      .sort(
        (a, b) =>
          new Date(b.lastChangedAt as string).getTime() -
          new Date(a.lastChangedAt as string).getTime(),
      )[0];

    if (newest && newest.event) {
      setFlash({ flashId: newest.id, flashEvent: newest.event });
      playKeywordSound(newest.event, WALL_SOUND_POLICY);
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setFlash(NO_FLASH), FLASH_MS);
    }
  }, [rows, ready]);

  // Clear the pending flash timer if the wall unmounts mid-throb.
  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  return flash;
}
